import { z } from 'zod';

/**
 * Contract e-signing (Documenso) - admin-facing views.
 * Server source: apps/api/src/modules/contracts.
 */

export const CONTRACT_STATUSES = [
  'DRAFT',
  'SENT',
  'VIEWED',
  'CUSTOMER_SIGNED',
  'COMPLETED',
  'DECLINED',
  'VOIDED',
] as const;

export const ContractStatusSchema = z.enum(CONTRACT_STATUSES);
export type ContractStatus = z.infer<typeof ContractStatusSchema>;

export const ContractListItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: ContractStatusSchema,
  bookingId: z.string().uuid().nullable(),
  itineraryId: z.string().uuid().nullable(),
  leadId: z.string().uuid().nullable(),
  customerId: z.string().uuid(),
  documensoDocumentId: z.number().nullable(),
  customerSignerName: z.string(),
  customerSignerEmail: z.string(),
  utazzoSignerName: z.string(),
  utazzoSignerEmail: z.string(),
  customerSigningUrl: z.string().nullable(),
  utazzoSigningUrl: z.string().nullable(),
  signedPdfKey: z.string().nullable(),
  sentAt: z.coerce.date().nullable(),
  viewedAt: z.coerce.date().nullable(),
  customerSignedAt: z.coerce.date().nullable(),
  utazzoSignedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  declinedAt: z.coerce.date().nullable(),
  declineReason: z.string().nullable(),
  createdAt: z.coerce.date(),
  customer: z
    .object({
      id: z.string().uuid(),
      displayName: z.string(),
      email: z.string().nullable(),
    })
    .optional(),
});
export type ContractListItem = z.infer<typeof ContractListItemSchema>;

export const ContractListSchema = z.array(ContractListItemSchema);

export const ContractEventSchema = z.object({
  id: z.string().uuid(),
  event: z.string(),
  createdAt: z.coerce.date(),
});
export type ContractEvent = z.infer<typeof ContractEventSchema>;

export const ContractDetailSchema = ContractListItemSchema.extend({
  events: z.array(ContractEventSchema).default([]),
});
export type ContractDetail = z.infer<typeof ContractDetailSchema>;

export const ContractDownloadSchema = z.object({
  url: z.string(),
  signed: z.boolean(),
});
export type ContractDownload = z.infer<typeof ContractDownloadSchema>;

/**
 * What the signing-link check found before sharing the link: `renewed` it had
 * expired on Documenso and was re-issued; `regenerated` the Documenso document
 * was gone and a fresh contract went out (`contractId` is the new one).
 */
export const ContractSigningLinkStateSchema = z.enum(['ok', 'renewed', 'regenerated', 'unchecked']);
export type ContractSigningLinkState = z.infer<typeof ContractSigningLinkStateSchema>;

export const ContractResendResultSchema = z.object({
  resentTo: z.array(z.string()),
  /** The customer also got the link on WhatsApp. */
  whatsapp: z.boolean().default(false),
  linkState: ContractSigningLinkStateSchema.default('unchecked'),
  contractId: z.string().uuid().optional(),
});
export type ContractResendResult = z.infer<typeof ContractResendResultSchema>;

/** "Copy link": the customer's current signing link (checked / renewed first). */
export const ContractSigningLinkSchema = z.object({
  url: z.string(),
  /** Documenso recipient expiry, when the instance reports one. */
  expiresAt: z.string().nullable(),
  linkState: ContractSigningLinkStateSchema,
  contractId: z.string().uuid(),
});
export type ContractSigningLink = z.infer<typeof ContractSigningLinkSchema>;
